import React from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import api from '../services/api';
import {
  BanknotesIcon,
  DocumentTextIcon,
  UsersIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

const fetchKPIs = () => api.get('/dashboard/kpi').then(res => res.data.data);
const fetchTrends = () => api.get('/dashboard/trends').then(res => res.data.data);

export default function Dashboard() {
  const { data: kpiData, isLoading: kpiLoading } = useQuery('dashboard-kpi', fetchKPIs);
  const { data: trendsData, isLoading: trendsLoading } = useQuery('dashboard-trends', fetchTrends);

  const kpi = kpiData?.kpi || {};
  const trends = trendsData?.trends || [];

  const stats = [
    { name: 'Total Bills', value: kpi.totalBills || 0, icon: DocumentTextIcon, color: 'text-blue-500 bg-blue-500/10 dark:bg-blue-500/20', href: '/bills' },
    { name: 'Paid Bills', value: kpi.paidBills || 0, icon: CheckCircleIcon, color: 'text-emerald-500 bg-emerald-500/10 dark:bg-emerald-500/20', href: '/payments' },
    { name: 'Unpaid Bills', value: kpi.unpaidBills || 0, icon: ClockIcon, color: 'text-amber-500 bg-amber-500/10 dark:bg-amber-500/20', href: '/bills' },
    { name: 'Blocked Bills', value: kpi.totalBlocked || 0, icon: ExclamationTriangleIcon, color: 'text-rose-500 bg-rose-500/10 dark:bg-rose-500/20', href: '/bills' },
    { name: 'Total Customers', value: kpi.totalCustomers || 0, icon: UsersIcon, color: 'text-indigo-500 bg-indigo-500/10 dark:bg-indigo-500/20', href: '/customers' },
    { name: 'Today Revenue', value: `PKR ${(kpi.todayRevenue || 0).toLocaleString()}`, icon: BanknotesIcon, color: 'text-teal-500 bg-teal-500/10 dark:bg-teal-500/20', href: '/reports' },
  ];

  if (kpiLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-96 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
        <p className="text-sm font-medium text-slate-500">Loading system metrics...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white tracking-tight">System Dashboard</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Real-time overview of payments, reconciliation, audits and billing metrics</p>
        </div>
        <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-1.5 text-xs text-slate-500 dark:text-slate-400 font-medium">
          <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
          Live updates online
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {stats.map((stat) => (
          <Link
            key={stat.name}
            to={stat.href}
            className="card group overflow-hidden"
          >
            <div className="card-body relative z-10 flex items-center">
              <div className={`flex-shrink-0 rounded-xl p-3.5 transition-all duration-300 group-hover:scale-110 ${stat.color}`}>
                <stat.icon className="h-6 w-6" aria-hidden="true" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="truncate text-xs font-semibold text-slate-400 uppercase tracking-wider">{stat.name}</dt>
                  <dd className="mt-1.5 text-2xl font-bold text-slate-900 dark:text-white tracking-tight">{stat.value}</dd>
                </dl>
              </div>
            </div>
            {/* Hover decorative overlay block */}
            <div className="absolute right-0 bottom-0 translate-x-4 translate-y-4 opacity-5 group-hover:opacity-10 transition-opacity duration-300 w-24 h-24 rounded-full bg-indigo-500 pointer-events-none" />
          </Link>
        ))}
      </div>

      {/* Collection Rate & Revenue Summary */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Collection Efficiency</h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 divide-y md:divide-y-0 md:divide-x divide-slate-200 dark:divide-slate-800">
            <div className="flex flex-col items-center justify-center p-4">
              <p className="text-3xl font-extrabold text-indigo-600 dark:text-indigo-400 tracking-tight">{kpi.collectionRate?.toFixed(2)}%</p>
              <p className="text-xs font-medium text-slate-400 uppercase tracking-widest mt-1.5">Collection Rate</p>
            </div>
            <div className="flex flex-col items-center justify-center p-4">
              <p className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">PKR {(kpi.monthRevenue || 0).toLocaleString()}</p>
              <p className="text-xs font-medium text-slate-400 uppercase tracking-widest mt-1.5">Monthly Revenue</p>
            </div>
            <div className="flex flex-col items-center justify-center p-4">
              <div className="flex items-center gap-2">
                <span className={`p-1 rounded-full ${kpi.monthGrowth >= 0 ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/20' : 'bg-rose-50 text-rose-600 dark:bg-rose-950/20'}`}>
                  {kpi.monthGrowth >= 0 ? (
                    <CheckCircleIcon className="h-5 w-5 text-emerald-500" />
                  ) : (
                    <ExclamationTriangleIcon className="h-5 w-5 text-rose-500" />
                  )}
                </span>
                <p className={`text-3xl font-extrabold tracking-tight ${kpi.monthGrowth >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {kpi.monthGrowth >= 0 ? '+' : ''}{kpi.monthGrowth?.toFixed(2)}%
                </p>
              </div>
              <p className="text-xs font-medium text-slate-400 uppercase tracking-widest mt-1.5">Month-on-Month Growth</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Daily Collection Trend</h3>
          </div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={trends}>
                <defs>
                  <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#4f46e5" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.3} />
                <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} tickLine={false} />
                <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', background: 'rgba(255, 255, 255, 0.95)', color: '#1e293b', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} />
                <Area type="monotone" dataKey="amount" stroke="#4f46e5" strokeWidth={2.5} fillOpacity={1} fill="url(#colorAmount)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Transaction Count</h3>
          </div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={trends}>
                <defs>
                  <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.95} />
                    <stop offset="95%" stopColor="#059669" stopOpacity={0.7} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.3} />
                <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} tickLine={false} />
                <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} tickFormatter={(tick) => Math.round(tick)} />
                <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', background: 'rgba(255, 255, 255, 0.95)', color: '#1e293b', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} />
                <Bar dataKey="count" fill="url(#colorCount)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="card">
        <div className="card-header flex justify-between items-center">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Recent Real-time Activity</h3>
          <Link to="/payments" className="text-sm font-semibold text-indigo-650 text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors">
            View all transactions →
          </Link>
        </div>
        <div className="card-body">
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Transaction ID</th>
                  <th>Challan Number</th>
                  <th>Customer Name</th>
                  <th>Paid Amount</th>
                  <th>Settlement Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800 bg-transparent">
                {kpiData?.recentTransactions?.slice(0, 5).map((txn) => (
                  <tr key={txn.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-900/30 transition-colors">
                    <td className="font-mono text-xs font-semibold text-indigo-600 dark:text-indigo-400">{txn.transactionNumber}</td>
                    <td>{txn.bill?.challanNo}</td>
                    <td className="font-medium text-slate-900 dark:text-white">{txn.bill?.consumerName}</td>
                    <td className="font-semibold text-slate-900 dark:text-white">PKR {parseFloat(txn.paymentAmount).toLocaleString()}</td>
                    <td>{new Date(txn.postedAt).toLocaleDateString()}</td>
                    <td>
                      <span className="badge badge-green">Success</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
